/**
 * Nut Sheet — Export to Excel (date range).
 *
 * Builds a two-sheet workbook from the range-mode response of
 * GET /nut-sheet/entries (see services/nutSheetApi.listNutSheetEntriesRange):
 *
 *   Sheet 1 "Summary" — the five summary cards (New / Used / Value Center /
 *     Totals / Wholesale) computed over the FULL range, mirroring
 *     components/nutSheet/SummaryTables.tsx exactly (by-type breakdown,
 *     Adjustments line, green After Adjustments panel). Value Center is
 *     skipped when the range has no VC sales, matching the page.
 *
 *   Sheet 2 "Entries" — one row per transaction, filtered by the page's row
 *     visibility toggles (the summary is NOT filtered — same semantics as the
 *     page, where toggles only affect the table). Date-block striping, native
 *     autofilter/sort, frozen ID columns, and a grand-total row outside the
 *     filter range.
 *
 * Styling follows the TSM report exports (TsmRankingReportModal.tsx).
 */

import type ExcelJSNS from 'exceljs';
import { loadExcelJS } from './loadExcelJS';
import {
  MtdSummary,
  NutSheetEntry,
  TypeRollup,
  isSwapRow,
  isValueCenterRow,
  isWholesaleRow,
} from '../types/nutSheet';

export interface NutSheetExportFilters {
  showWholesale: boolean;
  showOnlyWholesale: boolean;
  showOnlyValueCenter: boolean;
  excludeValueCenter: boolean;
  showSwap: boolean;
}

export interface NutSheetExportParams {
  locationName: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  entries: NutSheetEntry[]; // full range entries from the API
  summary: MtdSummary; // rangeSummary from the API (already reserve-netted when requested)
  excludeFiReserve: boolean;
  filters: NutSheetExportFilters;
}

// ── Shared style constants (TSM export palette) ──
const DARK = 'FF1F2937';
const GRAY = 'FF4B5563';
const MUTED = 'FF6B7280';
const HEADER_FILL = 'FF374151';
const SECTION_FILL = 'FFF3F4F6';
const ZEBRA_FILL = 'FFFAFAFA';
const SUBTOTAL_FILL = 'FFE5E7EB';
const GREEN_FILL = 'FFD1FAE5';
const GREEN_FONT = 'FF065F46';

const CURRENCY = '"$"#,##0';
const CURRENCY_SIGNED = '+"$"#,##0;-"$"#,##0;"$"0';

type Worksheet = ExcelJSNS.Worksheet;
type CellValue = ExcelJSNS.CellValue;

const thinBorder: Partial<ExcelJSNS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
  bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
  left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
  right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
};

function styleColumnHeaderCell(cell: ExcelJSNS.Cell, alignLeft: boolean) {
  cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } };
  cell.alignment = { horizontal: alignLeft ? 'left' : 'right', vertical: 'middle' };
  cell.border = {
    ...thinBorder,
    bottom: { style: 'medium', color: { argb: 'FF111827' } },
  };
}

/** Human description of the table's active row-visibility filters. */
export function describeFilters(filters: NutSheetExportFilters): string {
  if (filters.showOnlyWholesale) return 'Showing ONLY Wholesale rows (USED type W)';
  if (filters.showOnlyValueCenter) return 'Showing ONLY Value Center rows (USED type ending in VC)';
  const hidden: string[] = [];
  if (!filters.showWholesale) hidden.push('Wholesale rows hidden');
  if (filters.excludeValueCenter) hidden.push('Value Center rows hidden');
  if (!filters.showSwap) hidden.push('Swap rows hidden');
  return hidden.length > 0 ? hidden.join(' · ') : 'none — all rows shown';
}

/** Exact mirror of NutSheetTable's row-visibility logic. */
export function applyRowFilters(
  entries: NutSheetEntry[],
  filters: NutSheetExportFilters,
): NutSheetEntry[] {
  return entries.filter((e) => {
    if (filters.showOnlyWholesale) return isWholesaleRow(e);
    if (filters.showOnlyValueCenter) return isValueCenterRow(e);
    if (!filters.showWholesale && isWholesaleRow(e)) return false;
    if (filters.excludeValueCenter && isValueCenterRow(e)) return false;
    if (!filters.showSwap && isSwapRow(e)) return false;
    return true;
  });
}

export function composeVehicle(
  year: number | null,
  make: string | null,
  model: string | null,
  miles?: number | null,
): string {
  const parts = [year, make, model].filter((p) => p !== null && p !== '');
  let label = parts.join(' ');
  if (miles != null && label) label += ` · ${miles.toLocaleString()} mi`;
  return label;
}

/**
 * Canonical Nut Sheet output ordering: date, then NEW before USED within a
 * day, then by type, then entry order. Shared by the Excel Entries sheet and
 * the Print PDF view so the two outputs can never drift.
 */
export function sortNutSheetEntries(entries: NutSheetEntry[]): NutSheetEntry[] {
  return entries
    .slice()
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        b.category.localeCompare(a.category) ||
        a.type.localeCompare(b.type) ||
        a.createdAt.localeCompare(b.createdAt),
    );
}

/** Local-midnight Date for an ISO day string (avoids UTC off-by-one in Excel). */
function isoToLocalDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  return new Date(y, m - 1, d);
}

// ─────────────────────────────────────────────────────────────────────────────
// Sheet 1 — Summary
// ─────────────────────────────────────────────────────────────────────────────

const SUMMARY_COLS = 8;

function writeMergedLine(
  ws: Worksheet,
  row: number,
  text: string,
  font: Partial<ExcelJSNS.Font>,
  fill?: string,
) {
  const cell = ws.getCell(row, 1);
  cell.value = text;
  cell.font = font;
  if (fill) {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  }
  ws.mergeCells(row, 1, row, SUMMARY_COLS);
}

interface CardTotals {
  units: number;
  gross: number;
  doc: number;
  holdback: number;
  fAndI: number;
  adjustment: number;
}

function rollupTotals(rows: TypeRollup[]): CardTotals {
  return rows.reduce(
    (acc, r) => ({
      units: acc.units + r.units,
      gross: acc.gross + r.gross,
      doc: acc.doc + r.doc,
      holdback: acc.holdback + r.holdback,
      fAndI: acc.fAndI + r.fAndI,
      adjustment: acc.adjustment + r.adjustment,
    }),
    { units: 0, gross: 0, doc: 0, holdback: 0, fAndI: 0, adjustment: 0 },
  );
}

function writeMoneyRow(
  ws: Worksheet,
  row: number,
  values: (CellValue | null)[],
  opts: { bold?: boolean; fill?: string; fontColor?: string; numFmts?: (string | null)[] },
) {
  values.forEach((v, i) => {
    if (v === null) return;
    const cell = ws.getCell(row, i + 1);
    cell.value = v;
    cell.font = { size: 10, bold: opts.bold, color: { argb: opts.fontColor ?? DARK } };
    cell.alignment = { horizontal: i === 0 ? 'left' : 'right' };
    if (opts.fill) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.fill } };
    }
    const numFmt = opts.numFmts?.[i];
    if (numFmt) cell.numFmt = numFmt;
  });
}

/**
 * Write one summary card block; mirrors VehicleSummaryCard in
 * SummaryTables.tsx (pre-adjustment by-type rows, Adjustments line, After
 * Adjustments panel). Returns the next free row.
 */
function writeCardBlock(
  ws: Worksheet,
  startRow: number,
  title: string,
  rows: TypeRollup[],
  opts: {
    emptyLabel: string;
    subtitle?: string;
    showHoldback: boolean;
    emphasize: boolean;
  },
): number {
  let row = startRow;
  writeMergedLine(ws, row, title.toUpperCase(), { size: 11, bold: true, color: { argb: DARK } }, SECTION_FILL);
  row += 1;
  if (opts.subtitle) {
    writeMergedLine(ws, row, opts.subtitle, { size: 9, italic: true, color: { argb: MUTED } });
    row += 1;
  }

  if (rows.length === 0) {
    writeMergedLine(ws, row, `No ${opts.emptyLabel} transactions in this range.`, {
      size: 10,
      italic: true,
      color: { argb: MUTED },
    });
    return row + 2;
  }

  // By-type breakdown — Gross is PRE-adjustment (Σ Estimated), as on the page.
  const headers = opts.showHoldback
    ? ['Type', 'Units', 'Gross', 'Doc', 'Holdback', 'F&I']
    : ['Type', 'Units', 'Gross', 'Doc', 'F&I'];
  headers.forEach((h, i) => styleColumnHeaderCell(ws.getCell(row, i + 1), i === 0));
  headers.forEach((h, i) => { ws.getCell(row, i + 1).value = h; });
  row += 1;

  const moneyFmts = opts.showHoldback
    ? [null, '#,##0', CURRENCY, CURRENCY, CURRENCY, CURRENCY]
    : [null, '#,##0', CURRENCY, CURRENCY, CURRENCY];

  rows.forEach((r, i) => {
    // Swap rollups carry 0 units; show the real excluded count in parentheses,
    // matching the page cards.
    const units: CellValue = r.excludedUnits ? `(${r.excludedUnits})` : r.units;
    const values: CellValue[] = opts.showHoldback
      ? [r.type, units, r.gross, r.doc, r.holdback, r.fAndI]
      : [r.type, units, r.gross, r.doc, r.fAndI];
    writeMoneyRow(ws, row, values, {
      fill: i % 2 === 1 ? ZEBRA_FILL : undefined,
      numFmts: moneyFmts,
    });
    row += 1;
  });

  const t = rollupTotals(rows);
  const totalValues: CellValue[] = opts.showHoldback
    ? ['Total', t.units, t.gross, t.doc, t.holdback, t.fAndI]
    : ['Total', t.units, t.gross, t.doc, t.fAndI];
  writeMoneyRow(ws, row, totalValues, { bold: true, fill: SUBTOTAL_FILL, numFmts: moneyFmts });
  row += 1;

  if (rows.some((r) => (r.excludedUnits ?? 0) > 0)) {
    writeMergedLine(ws, row, 'Units in ( ) are Swaps — $ included, not counted in unit totals.', {
      size: 9,
      italic: true,
      color: { argb: MUTED },
    });
    row += 1;
  }

  row = writeAdjustmentsAndAfter(ws, row, t, opts.showHoldback, opts.emphasize);
  return row + 1; // blank spacer between cards
}

/**
 * The Adjustments line + After Adjustments panel shared by every card.
 * Post-adjustment gross = Est − Σ(Est − Actual) = Actual; the page displays
 * the sign-flipped stored adjustment (Σ Actual − Est).
 */
function writeAdjustmentsAndAfter(
  ws: Worksheet,
  startRow: number,
  t: CardTotals,
  showHoldback: boolean,
  emphasize: boolean,
): number {
  let row = startRow;
  const grossAfter = t.gross - t.adjustment;
  const totalAfter = grossAfter + t.doc + t.holdback + t.fAndI;
  const avgAfter = t.units > 0 ? totalAfter / t.units : 0;

  const adjLabel = ws.getCell(row, 1);
  adjLabel.value = 'Adjustments (Σ Actual − Est):';
  adjLabel.font = { size: 10, color: { argb: GRAY } };
  ws.mergeCells(row, 1, row, 2);
  const adjValue = ws.getCell(row, 3);
  adjValue.value = grossAfter - t.gross; // = −Σ(Est − Actual)
  adjValue.numFmt = CURRENCY_SIGNED;
  adjValue.font = { size: 10, color: { argb: DARK } };
  adjValue.alignment = { horizontal: 'right' };
  row += 1;

  const fontColor = emphasize ? GREEN_FONT : DARK;
  const fill = emphasize ? GREEN_FILL : undefined;

  const afterTitle = ws.getCell(row, 1);
  afterTitle.value = 'AFTER ADJUSTMENTS';
  afterTitle.font = { size: 10, bold: true, color: { argb: fontColor } };
  if (fill) {
    for (let c = 1; c <= SUMMARY_COLS; c++) {
      ws.getCell(row, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
    }
  }
  ws.mergeCells(row, 1, row, SUMMARY_COLS);
  row += 1;

  const headers = showHoldback
    ? ['Units', 'Gross', 'Doc', 'Holdback', 'F&I', 'Total', 'Avg / Unit']
    : ['Units', 'Gross', 'Doc', 'F&I', 'Total', 'Avg / Unit'];
  const values: CellValue[] = showHoldback
    ? [t.units, grossAfter, t.doc, t.holdback, t.fAndI, totalAfter, avgAfter]
    : [t.units, grossAfter, t.doc, t.fAndI, totalAfter, avgAfter];
  const fmts = ['#,##0', ...headers.slice(1).map(() => CURRENCY)];

  headers.forEach((h, i) => {
    const cell = ws.getCell(row, i + 1);
    cell.value = h;
    cell.font = { size: 9, bold: true, color: { argb: fontColor } };
    cell.alignment = { horizontal: 'right' };
    if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
  });
  row += 1;
  writeMoneyRow(ws, row, values, { bold: true, fill, fontColor, numFmts: fmts });
  return row + 1;
}

/** Totals card — written from summary.totals + summary.adjustments (no by-type table). */
function writeTotalsCard(ws: Worksheet, startRow: number, summary: MtdSummary): number {
  let row = startRow;
  writeMergedLine(ws, row, 'TOTALS (RANGE)', { size: 11, bold: true, color: { argb: DARK } }, SECTION_FILL);
  row += 1;
  writeMergedLine(ws, row, 'Excludes Wholesale & Value Center · Swap $ included, Swap units not counted', { size: 9, italic: true, color: { argb: MUTED } });
  row += 1;

  const t = summary.totals;
  const total = t.gross + t.doc + t.holdback + t.fAndI;
  const avgPerUnit = t.units > 0 ? total / t.units : 0;

  const headers = ['Units', 'Gross', 'Doc', 'Holdback', 'F&I', 'Total', 'Avg / Unit'];
  headers.forEach((h, i) => {
    styleColumnHeaderCell(ws.getCell(row, i + 1), false);
    ws.getCell(row, i + 1).value = h;
  });
  row += 1;
  const fmts = ['#,##0', CURRENCY, CURRENCY, CURRENCY, CURRENCY, CURRENCY, CURRENCY];
  writeMoneyRow(ws, row, [t.units, t.gross, t.doc, t.holdback, t.fAndI, total, avgPerUnit], { numFmts: fmts });
  row += 1;

  row = writeAdjustmentsAndAfter(
    ws,
    row,
    { ...t, adjustment: summary.adjustments },
    true,
    true,
  );
  return row + 1;
}

function buildSummarySheet(
  wb: ExcelJSNS.Workbook,
  params: NutSheetExportParams,
  exportedAt: Date,
) {
  const ws = wb.addWorksheet('Summary', {
    properties: { defaultRowHeight: 16 },
    pageSetup: { orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  [24, 10, 14, 12, 12, 14, 14, 14].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

  writeMergedLine(ws, 1, `Nut Sheet — ${params.locationName}`, { size: 16, bold: true, color: { argb: DARK } });
  writeMergedLine(ws, 2, `Date range: ${params.startDate} to ${params.endDate} · ${params.entries.length} entries`, { size: 11, color: { argb: GRAY } });
  writeMergedLine(
    ws, 3,
    `Exported ${exportedAt.toLocaleString('en-US')} · F&I Reserve: ${params.excludeFiReserve ? 'Excluded (all F&I figures net of reserve)' : 'Included'}`,
    { size: 10, italic: true, color: { argb: MUTED } },
  );
  writeMergedLine(
    ws, 4,
    `Summary covers ALL entries in the range. Row filters apply to the Entries sheet only: ${describeFilters(params.filters)}`,
    { size: 9, italic: true, color: { argb: MUTED } },
  );

  const s = params.summary;
  let row = 6;
  row = writeCardBlock(ws, row, 'New Vehicle Summary (Range)', s.byType.filter((r) => r.category === 'NEW'), {
    emptyLabel: 'NEW', showHoldback: true, emphasize: true,
  });
  row = writeCardBlock(ws, row, 'Used Vehicle Summary (Range)', s.byType.filter((r) => r.category === 'USED'), {
    emptyLabel: 'USED', showHoldback: false, emphasize: true,
  });
  // Value Center hidden entirely when the range has none — matches the page.
  if (s.valueCenter.byType.length > 0) {
    row = writeCardBlock(ws, row, 'Value Center Summary (Range)', s.valueCenter.byType, {
      emptyLabel: 'VALUE CENTER', showHoldback: false, emphasize: true,
    });
  }
  row = writeTotalsCard(ws, row, s);
  writeCardBlock(ws, row, 'Wholesale Vehicle Summary (Range)', s.wholesale.byType, {
    emptyLabel: 'WHOLESALE',
    subtitle: 'Dealer-to-dealer (USED type W) — excluded from Totals',
    showHoldback: false,
    emphasize: false,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Sheet 2 — Entries
// ─────────────────────────────────────────────────────────────────────────────

interface ColSpec {
  header: string;
  width: number;
  align: 'left' | 'right' | 'center';
  numFmt?: string;
  /** Currency numFmt applied only when the value is a number (mixed Retail columns). */
  currencyIfNumber?: boolean;
  bold?: boolean;
  /** Include this column in the grand-total row. */
  sum?: boolean;
  value: (e: NutSheetEntry) => CellValue;
}

function buildEntryColumns(excludeFiReserve: boolean): ColSpec[] {
  const cols: ColSpec[] = [
    { header: 'Date', width: 11, align: 'left', numFmt: 'mm/dd/yyyy', value: (e) => isoToLocalDate(e.date) },
    { header: 'Cat', width: 7, align: 'left', value: (e) => e.category },
    { header: 'Type', width: 7, align: 'left', value: (e) => e.type },
    { header: 'Deal #', width: 10, align: 'left', value: (e) => e.dealNumber ?? '' },
    { header: 'Stock #', width: 10, align: 'left', value: (e) => e.stockNumber },
    { header: 'Sold Vehicle', width: 24, align: 'left', value: (e) => composeVehicle(e.soldYear, e.soldMake, e.soldModel) },
    { header: 'Customer', width: 14, align: 'left', value: (e) => e.customerLastName ?? '' },
    { header: 'Salesperson', width: 16, align: 'left', value: (e) => e.salesperson ?? '' },
    { header: 'Salesperson 2', width: 14, align: 'left', value: (e) => e.salesperson2 ?? '' },
    { header: 'Sales Mgr', width: 16, align: 'left', value: (e) => e.salesManager ?? '' },
    { header: 'F&I Rep', width: 14, align: 'left', value: (e) => e.fiRep ?? '' },
    { header: 'Days in Stock', width: 8, align: 'right', numFmt: '0', value: (e) => e.daysInStock },
    { header: 'Est. Gross', width: 11, align: 'right', numFmt: CURRENCY, sum: true, value: (e) => e.estimatedGross },
    { header: 'Actual Gross', width: 11, align: 'right', numFmt: CURRENCY, sum: true, value: (e) => e.actualGross },
    {
      header: 'Adj (Act−Est)', width: 12, align: 'right', numFmt: CURRENCY_SIGNED, sum: true,
      value: (e) =>
        e.estimatedGross === null && e.actualGross === null
          ? null
          : (e.actualGross ?? 0) - (e.estimatedGross ?? 0),
    },
    { header: 'Acct Comment', width: 22, align: 'left', value: (e) => e.accountingComment ?? '' },
    { header: 'Deal Comment', width: 26, align: 'left', value: (e) => e.comment ?? '' },
    { header: 'F&I', width: 10, align: 'right', numFmt: CURRENCY, sum: true, value: (e) => e.fAndI },
  ];

  if (excludeFiReserve) {
    cols.push(
      { header: 'F&I Reserve', width: 11, align: 'right', numFmt: CURRENCY, sum: true, value: (e) => e.financeReserve },
      {
        // The pay-plan number when reserve is excluded — bolded to stand out.
        header: 'Net F&I', width: 11, align: 'right', numFmt: CURRENCY, bold: true, sum: true,
        value: (e) => (e.fAndI === null && e.financeReserve === null ? null : (e.fAndI ?? 0) - (e.financeReserve ?? 0)),
      },
    );
  }

  cols.push(
    { header: 'Back End $', width: 10, align: 'right', numFmt: CURRENCY, sum: true, value: (e) => e.points },
    { header: 'Holdback', width: 10, align: 'right', numFmt: CURRENCY, sum: true, value: (e) => e.holdback },
    { header: 'Doc', width: 9, align: 'right', numFmt: CURRENCY, sum: true, value: (e) => e.doc },
    { header: 'Fin Source', width: 9, align: 'left', value: (e) => e.financeSource ?? '' },
    {
      // Y/N on retail rows only — Wholesale/Swap rows have no CSI toggle in the UI.
      header: 'CSI', width: 6, align: 'center',
      value: (e) => (isWholesaleRow(e) || isSwapRow(e) ? '' : e.csiBonus ? 'Y' : 'N'),
    },
    { header: 'T1 Stock #', width: 10, align: 'left', value: (e) => e.tradeStockNumber ?? '' },
    { header: 'T1 Vehicle', width: 24, align: 'left', value: (e) => composeVehicle(e.tradeYear, e.tradeMake, e.tradeModel, e.tradeMiles) },
    { header: 'T1 ACV', width: 10, align: 'right', numFmt: CURRENCY, value: (e) => e.acv },
    { header: 'T1 Retail', width: 10, align: 'right', currencyIfNumber: true, value: (e) => e.retail },
    { header: 'T1 Plan', width: 8, align: 'left', value: (e) => e.t1Plan ?? '' },
    { header: 'T1 Comment', width: 18, align: 'left', value: (e) => e.tradeComment ?? '' },
    { header: 'T2 Stock #', width: 10, align: 'left', value: (e) => e.trade2StockNumber ?? '' },
    { header: 'T2 Vehicle', width: 24, align: 'left', value: (e) => composeVehicle(e.trade2Year, e.trade2Make, e.trade2Model, e.trade2Miles) },
    { header: 'T2 ACV', width: 10, align: 'right', numFmt: CURRENCY, value: (e) => e.trade2Acv },
    { header: 'T2 Retail', width: 10, align: 'right', currencyIfNumber: true, value: (e) => e.trade2Retail },
    { header: 'T2 Plan', width: 8, align: 'left', value: (e) => e.t2Plan ?? '' },
    { header: 'T2 Comment', width: 18, align: 'left', value: (e) => e.trade2Comment ?? '' },
    { header: 'Open ROs', width: 14, align: 'left', value: (e) => e.openROs ?? '' },
  );
  return cols;
}

function buildEntriesSheet(
  wb: ExcelJSNS.Workbook,
  params: NutSheetExportParams,
  exportedAt: Date,
) {
  const cols = buildEntryColumns(params.excludeFiReserve);
  const HEADER_ROW = 6;

  const ws = wb.addWorksheet('Entries', {
    // Pin Date/Cat/Type/Deal # while scrolling right; header block while scrolling down.
    views: [{ state: 'frozen', xSplit: 4, ySplit: HEADER_ROW }],
    properties: { defaultRowHeight: 16 },
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  cols.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

  const visible = sortNutSheetEntries(applyRowFilters(params.entries, params.filters));

  const writeTitleLine = (row: number, text: string, font: Partial<ExcelJSNS.Font>) => {
    const cell = ws.getCell(row, 1);
    cell.value = text;
    cell.font = font;
    ws.mergeCells(row, 1, row, cols.length);
  };
  writeTitleLine(1, `Nut Sheet Entries — ${params.locationName}`, { size: 16, bold: true, color: { argb: DARK } });
  writeTitleLine(2, `Date range: ${params.startDate} to ${params.endDate} · ${visible.length} of ${params.entries.length} entries shown`, { size: 11, color: { argb: GRAY } });
  writeTitleLine(3, `Exported ${exportedAt.toLocaleString('en-US')} · F&I Reserve: ${params.excludeFiReserve ? 'Excluded (Net F&I column = F&I − Reserve)' : 'Included'}`, { size: 10, italic: true, color: { argb: MUTED } });
  writeTitleLine(4, `Row filters: ${describeFilters(params.filters)} · Use the header dropdowns to sort/filter`, { size: 9, italic: true, color: { argb: MUTED } });

  const headerRow = ws.getRow(HEADER_ROW);
  cols.forEach((c, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = c.header;
    styleColumnHeaderCell(cell, c.align === 'left');
  });
  headerRow.height = 22;

  // Data rows — zebra-striped by DATE BLOCK (all rows of one day share a
  // stripe) with a medium top border + bold Date cell on each new day.
  let stripeOn = false;
  let prevDate: string | null = null;
  visible.forEach((entry, i) => {
    const r = ws.getRow(HEADER_ROW + 1 + i);
    const isNewDay = entry.date !== prevDate;
    if (isNewDay) {
      stripeOn = prevDate === null ? false : !stripeOn;
      prevDate = entry.date;
    }

    cols.forEach((c, ci) => {
      const cell = r.getCell(ci + 1);
      const v = c.value(entry);
      cell.value = v;
      cell.alignment = { horizontal: c.align };
      cell.font = { size: 9, bold: (ci === 0 && isNewDay) || c.bold, color: { argb: DARK } };
      if (c.numFmt) cell.numFmt = c.numFmt;
      if (c.currencyIfNumber && typeof v === 'number') cell.numFmt = CURRENCY;
      if (stripeOn) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA_FILL } };
      }
      if (isNewDay) {
        cell.border = { top: { style: 'medium', color: { argb: 'FF9CA3AF' } } };
      }
    });
  });

  const lastDataRow = HEADER_ROW + visible.length;
  ws.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: lastDataRow, column: cols.length },
  };

  // Grand-total row — one blank row below the data so it stays OUTSIDE the
  // autofilter range (sorting/filtering in Excel won't drag it around).
  // These sum only the rows shown here; the authoritative range totals
  // (wholesale/VC split out) live on the Summary sheet.
  const totalRow = ws.getRow(lastDataRow + 2);
  cols.forEach((c, ci) => {
    const cell = totalRow.getCell(ci + 1);
    cell.font = { size: 9, bold: true, color: { argb: DARK } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SECTION_FILL } };
    cell.border = { top: { style: 'double', color: { argb: 'FF111827' } } };
    cell.alignment = { horizontal: c.align };
    if (ci === 0) {
      cell.value = `TOTAL — ${visible.length} entries shown`;
    } else if (c.sum) {
      cell.value = visible.reduce((acc, e) => {
        const v = c.value(e);
        return acc + (typeof v === 'number' ? v : 0);
      }, 0);
      cell.numFmt = c.numFmt ?? CURRENCY;
    }
  });
  ws.mergeCells(lastDataRow + 2, 1, lastDataRow + 2, 5);
}

// ─────────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────────

export async function exportNutSheetToExcel(params: NutSheetExportParams): Promise<void> {
  // Lazy-load exceljs only when the user actually exports — keeps the initial
  // bundle lean (same pattern as the TSM report exports). loadExcelJS self-heals
  // the post-deploy stale-chunk case (see utils/loadExcelJS.ts).
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  wb.creator = "Darling's Dashboard";
  const exportedAt = new Date();
  wb.created = exportedAt;

  buildSummarySheet(wb, params, exportedAt);
  buildEntriesSheet(wb, params, exportedAt);

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const locSlug = params.locationName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '');
  link.download = `NutSheet-${locSlug}-${params.startDate}_to_${params.endDate}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}
